import { useEffect, useState } from 'react';
import { Loader2, User } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/auth/AuthProvider';
import { ImageUpload } from '@/components/ui/image-upload';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { UserService } from '@/services/users/UserService';
import { LANGUAGE_FLAGS } from '@/utils/localization/language';

// Common timezones
const TIMEZONES = [
  "UTC",
  "Atlantic/Azores",
  "Atlantic/Cape_Verde",
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "America/Anchorage",
  "Pacific/Honolulu",
  "Europe/London",
  "Europe/Paris",
  "Europe/Berlin",
  "Europe/Moscow",
  "Europe/Istanbul",
  "Asia/Dubai",
  "Asia/Kolkata",
  "Asia/Bangkok",
  "Asia/Singapore",
  "Asia/Hong_Kong",
  "Asia/Shanghai",
  "Asia/Tokyo",
  "Asia/Seoul",
  "Asia/Manila",
  "Pacific/Samoa",
  "Australia/Sydney",
  "Australia/Melbourne",
  "Australia/Brisbane",
  "Australia/Perth",
  "Australia/Adelaide",
  "Pacific/Auckland",
  "Pacific/Fiji",
];

export const ProfileSection = () => {
  const { user, userProfile } = useAuth();
  const [fullName, setFullName] = useState("");
  const [avatar, setAvatar] = useState<string | undefined>("");
  const [timezone, setTimezone] = useState("");
  const [useCustomTimezone, setUseCustomTimezone] = useState(false);
  const [preferredLanguage, setPreferredLanguage] = useState("");
  const [isExtraDetailsOpen, setIsExtraDetailsOpen] = useState(false);
  const [isLoadingExtra, setIsLoadingExtra] = useState(false);

  const [phoneNumber, setPhoneNumber] = useState("");
  const [phoneCountryCode, setPhoneCountryCode] = useState("");
  const [addressLine1, setAddressLine1] = useState("");
  const [addressLine2, setAddressLine2] = useState("");
  const [city, setCity] = useState("");
  const [stateProvince, setStateProvince] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [country, setCountry] = useState("");
  const [dateOfBirth, setDateOfBirth] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [jobTitle, setJobTitle] = useState("");

  useEffect(() => {
    if (user && userProfile) {
      setFullName(userProfile.full_name || "");
      setAvatar(userProfile.avatar_url || undefined);
      const tz = userProfile.timezone || "";
      setTimezone(tz);
      setUseCustomTimezone(!TIMEZONES.includes(tz));

      setPhoneNumber(userProfile.phone_number || "");
      setPhoneCountryCode(userProfile.phone_country_code || "");
      setAddressLine1(userProfile.address_line1 || "");
      setAddressLine2(userProfile.address_line2 || "");
      setCity(userProfile.city || "");
      setStateProvince(userProfile.state_province || "");
      setPostalCode(userProfile.postal_code || "");
      setCountry(userProfile.country || "");
      setDateOfBirth(userProfile.date_of_birth || "");
      setPreferredLanguage(userProfile.preferred_language || "");
      setCompanyName(userProfile.company_name || "");
      setJobTitle(userProfile.job_title || "");
    }
  }, [user, userProfile]);

  const handleUpdateProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    try {
      const updates = {
        full_name: fullName,
        avatar_url: avatar,
        timezone: timezone || null,
        preferred_language: preferredLanguage || null,
      };

      await UserService.updateUserProfile(user.id, updates);
      toast.success("Profile updated successfully!");
    } catch (err: any) {
      toast.error(`Failed to update profile: ${err.message}`);
    }
  };

  const handleUpdateExtraDetails = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    setIsLoadingExtra(true);
    try {
      const updates = {
        phone_number: phoneNumber || null,
        phone_country_code: phoneCountryCode || null,
        address_line1: addressLine1 || null,
        address_line2: addressLine2 || null,
        city: city || null,
        state_province: stateProvince || null,
        postal_code: postalCode || null,
        country: country || null,
        date_of_birth: dateOfBirth || null,
        company_name: companyName || null,
        job_title: jobTitle || null,
      };

      await UserService.updateUserProfile(user.id, updates);
      setIsExtraDetailsOpen(false);
      toast.success("Extra details updated successfully!");
    } catch (err: any) {
      toast.error(`Failed to update details: ${err.message}`);
    } finally {
      setIsLoadingExtra(false);
    }
  };

  return (
    <div className="bg-white rounded-[10px] p-5 shadow-[0_3px_10px_rgba(0,0,0,0.05)]">
      <div className="flex items-center justify-between mb-[18px]">
        <div className="flex items-center">
          <div className="h-8 w-8 rounded-full bg-genie-primary/10 flex items-center justify-center mr-3">
            <User className="h-5 w-5 text-genie-primary" />
          </div>
          <h2 className="text-xl font-semibold">Profile</h2>
        </div>
        <div className="flex items-center">
          {avatar ? (
            <img
              src={avatar}
              alt="Profile"
              className="h-10 w-10 rounded-full object-cover border-2 border-gray-200"
            />
          ) : (
            <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
              <User className="h-5 w-5 text-gray-400" />
            </div>
          )}
        </div>
      </div>
      <form onSubmit={handleUpdateProfile} className="flex flex-col gap-[14px]">
        <div>
          <label className="block mb-1.5 font-medium text-sm">Email Address</label>
          <input
            type="email"
            value={user?.email || ""}
            disabled
            className="w-full p-2.5 rounded-md border border-gray-300 bg-gray-100 cursor-not-allowed text-sm"
          />
          <p className="text-xs text-gray-500 mt-1 text-right">
            Member since {user?.created_at ? new Date(user.created_at).toLocaleDateString() : "—"}
          </p>
        </div>
        <div>
          <ImageUpload
            bucket="genie-images"
            value={avatar}
            onChange={setAvatar}
            label="Profile Avatar"
            className="w-full"
          />
        </div>
        <div>
          <label htmlFor="fullName" className="block mb-1.5 font-medium text-sm">
            Full Name
          </label>
          <input
            id="fullName"
            type="text"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            className="w-full p-[9px] rounded-md border border-gray-300 text-sm"
          />
        </div>
        <div>
          <label htmlFor="preferredLanguage" className="block mb-1.5 font-medium text-sm">
            Preferred Language
          </label>
          <select
            id="preferredLanguage"
            value={preferredLanguage}
            onChange={(e) => setPreferredLanguage(e.target.value)}
            className="w-full p-[9px] rounded-md border border-gray-300 text-sm"
          >
            <option value="">Select a language...</option>
            {Object.entries(LANGUAGE_FLAGS)
              .filter(([code]) => code !== "default")
              .sort(([, a], [, b]) => a.name.localeCompare(b.name))
              .map(([code, { flag, name }]) => (
                <option key={code} value={code}>
                  {flag} {name}
                </option>
              ))}
          </select>
        </div>
        <div>
          <label htmlFor="timezone" className="block mb-1.5 font-medium text-sm">
            Timezone
          </label>
          {useCustomTimezone ? (
            <div className="flex gap-2">
              <input
                id="timezone"
                type="text"
                value={timezone}
                onChange={(e) => setTimezone(e.target.value)}
                placeholder="Enter custom timezone (e.g., UTC+5:30)"
                className="flex-1 p-[9px] rounded-md border border-gray-300 text-sm"
              />
              <button
                type="button"
                onClick={() => setUseCustomTimezone(false)}
                className="px-3 py-1 bg-gray-200 hover:bg-gray-300 rounded-md text-sm font-medium"
              >
                Use List
              </button>
            </div>
          ) : (
            <div className="flex gap-2">
              <select
                id="timezone"
                value={timezone || ""}
                onChange={(e) => {
                  const value = e.target.value;
                  setTimezone(value);
                  if (value && !TIMEZONES.includes(value)) {
                    setUseCustomTimezone(true);
                  }
                }}
                className="flex-1 p-[9px] rounded-md border border-gray-300 text-sm"
              >
                <option value="">Select timezone...</option>
                {TIMEZONES.map((tz) => (
                  <option key={tz} value={tz}>
                    {tz}
                  </option>
                ))}
              </select>
              <button
                type="button"
                onClick={() => setUseCustomTimezone(true)}
                className="px-3 py-1 bg-gray-200 hover:bg-gray-300 rounded-md text-sm font-medium"
              >
                Custom
              </button>
            </div>
          )}
        </div>
        <div className="flex gap-2">
          <button
            type="submit"
            className="text-white border-none rounded-md px-3.5 py-[7px] font-semibold cursor-pointer text-sm bg-genie-primary"
          >
            Save Profile
          </button>
          <button
            type="button"
            onClick={() => setIsExtraDetailsOpen(true)}
            className="text-genie-primary border border-genie-primary rounded-md px-3.5 py-[7px] font-semibold cursor-pointer text-sm bg-transparent hover:bg-genie-primary/5"
          >
            Edit Extra Details
          </button>
        </div>
      </form>

      <Sheet open={isExtraDetailsOpen} onOpenChange={setIsExtraDetailsOpen}>
        <SheetContent
          side="right"
          showClose={false}
          className="w-full sm:max-w-xl flex flex-col h-full p-0"
        >
          <div className="flex items-center justify-between border-b p-6 sm:p-8">
            <div>
              <h2 className="text-xl font-bold text-gray-900">Extra Details</h2>
              <p className="text-sm text-gray-500 mt-1">Edit additional profile information</p>
            </div>
            <button
              onClick={() => setIsExtraDetailsOpen(false)}
              className="text-gray-400 hover:text-gray-600 text-2xl font-light transition-colors"
            >
              &times;
            </button>
          </div>

          <form
            onSubmit={handleUpdateExtraDetails}
            className="flex-1 overflow-y-auto px-6 sm:px-8 py-6 space-y-4"
          >
            <div>
              <label className="block mb-1.5 font-medium text-sm">Phone Number</label>
              <input
                type="tel"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                className="w-full p-[9px] rounded-md border border-gray-300 text-sm"
                placeholder="e.g., +1234567890"
              />
            </div>

            <div>
              <label className="block mb-1.5 font-medium text-sm">Phone Country Code</label>
              <input
                type="text"
                value={phoneCountryCode}
                onChange={(e) => setPhoneCountryCode(e.target.value)}
                className="w-full p-[9px] rounded-md border border-gray-300 text-sm"
                placeholder="e.g., US, CA, UK"
              />
            </div>

            <div>
              <label className="block mb-1.5 font-medium text-sm">Address Line 1</label>
              <input
                type="text"
                value={addressLine1}
                onChange={(e) => setAddressLine1(e.target.value)}
                className="w-full p-[9px] rounded-md border border-gray-300 text-sm"
                placeholder="Street address"
              />
            </div>

            <div>
              <label className="block mb-1.5 font-medium text-sm">Address Line 2</label>
              <input
                type="text"
                value={addressLine2}
                onChange={(e) => setAddressLine2(e.target.value)}
                className="w-full p-[9px] rounded-md border border-gray-300 text-sm"
                placeholder="Apartment, suite, etc."
              />
            </div>

            <div>
              <label className="block mb-1.5 font-medium text-sm">City</label>
              <input
                type="text"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                className="w-full p-[9px] rounded-md border border-gray-300 text-sm"
              />
            </div>

            <div>
              <label className="block mb-1.5 font-medium text-sm">State/Province</label>
              <input
                type="text"
                value={stateProvince}
                onChange={(e) => setStateProvince(e.target.value)}
                className="w-full p-[9px] rounded-md border border-gray-300 text-sm"
              />
            </div>

            <div>
              <label className="block mb-1.5 font-medium text-sm">Postal Code</label>
              <input
                type="text"
                value={postalCode}
                onChange={(e) => setPostalCode(e.target.value)}
                className="w-full p-[9px] rounded-md border border-gray-300 text-sm"
              />
            </div>

            <div>
              <label className="block mb-1.5 font-medium text-sm">Country</label>
              <input
                type="text"
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                className="w-full p-[9px] rounded-md border border-gray-300 text-sm"
              />
            </div>

            <div>
              <label className="block mb-1.5 font-medium text-sm">Date of Birth</label>
              <input
                type="date"
                value={dateOfBirth}
                onChange={(e) => setDateOfBirth(e.target.value)}
                className="w-full p-[9px] rounded-md border border-gray-300 text-sm"
              />
            </div>

            <div>
              <label className="block mb-1.5 font-medium text-sm">Company Name</label>
              <input
                type="text"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                className="w-full p-[9px] rounded-md border border-gray-300 text-sm"
              />
            </div>

            <div>
              <label className="block mb-1.5 font-medium text-sm">Job Title</label>
              <input
                type="text"
                value={jobTitle}
                onChange={(e) => setJobTitle(e.target.value)}
                className="w-full p-[9px] rounded-md border border-gray-300 text-sm"
              />
            </div>

            {userProfile?.billing_currency && (
              <div>
                <label className="block mb-1.5 font-medium text-sm">Billing Currency</label>
                <input
                  type="text"
                  value={userProfile.billing_currency}
                  disabled
                  className="w-full p-[9px] rounded-md border border-gray-300 bg-gray-100 cursor-not-allowed text-sm"
                />
              </div>
            )}
          </form>

          <div className="sticky bottom-0 border-t px-6 sm:px-8 py-4 flex justify-start gap-3 bg-white">
            <button
              type="button"
              onClick={() => setIsExtraDetailsOpen(false)}
              className="bg-transparent border border-gray-300 text-gray-600 px-4 py-2 rounded-md cursor-pointer text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isLoadingExtra}
            >
              Cancel
            </button>
            <button
              onClick={handleUpdateExtraDetails}
              disabled={isLoadingExtra}
              className="bg-genie-primary text-white px-6 py-2 rounded-md cursor-pointer text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isLoadingExtra && <Loader2 className="h-4 w-4 animate-spin" />}
              {isLoadingExtra ? "Saving..." : "Save Details"}
            </button>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
};
